package com.evoapps.evoshift.domain.staffing

import com.evoapps.evoshift.domain.market.Market
import com.evoapps.evoshift.domain.model.LabourBase
import com.evoapps.evoshift.domain.model.LabourShare
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.minus

/*
 * SH-54: staffing hints, rules only. Each day of a month gets a verdict, "too few", "just right" or
 * "too many", from its planned labour cost against the income the day is expected to bring
 * (SH-95, [LabourShare]):
 *
 *   expected income = expected net sales × commission % + the day's fixed revenue
 *                     (a workplace without a commission: the expected net sales)
 *   labour %        = planned employer labour cost ÷ expected income × 100
 *
 * inside the workplace's target band ([LabourTarget], target ± 5 points) the day is just right,
 * above it too many, below it too few. The day's unassigned shifts are planned work too: their hours
 * count at the month's average cost per hour (else the team's from their pay terms) as an estimate,
 * or, when no cost per hour is known, are left out and said to be. Deterministic and computed here, so a manager can redo it by
 * hand; the screens word the facts (UiText convention). An AI explanation on top is a later step.
 */

/**
 * The labour share a workplace aims for: planned employer labour cost as a share of the day's
 * expected income ([LabourShare]: the commission on the sales plus the fixed revenue, or the net
 * sales without a commission), in percent, with [tolerancePoints] either side ("60% ± 5" = 55–65%).
 * Measured like every labour % in the app (the daily log, the dashboards).
 */
data class LabourTarget(
    val percent: Double,
    val tolerancePoints: Double = DEFAULT_TOLERANCE_POINTS
) {
    init {
        require(percent > 0) { "The labour target must be above zero" }
        require(tolerancePoints >= 0) { "The tolerance cannot be negative" }
    }

    /** The lowest labour % still just right. */
    val low: Double get() = (percent - tolerancePoints).coerceAtLeast(0.0)

    /** The highest labour % still just right. */
    val high: Double get() = percent + tolerancePoints

    companion object {
        const val DEFAULT_TOLERANCE_POINTS = 5.0
        /** The targets the settings accept, in percent. */
        const val MIN_PERCENT = 1.0
        const val MAX_PERCENT = 100.0

        /** [market]'s default for a workplace with [commissionPercent] (a fraction; 0 = none). */
        fun defaultFor(market: Market, commissionPercent: Double = 0.0): LabourTarget =
            LabourTarget(market.defaultLabourPercentFor(commissionPercent))
    }
}

enum class StaffingVerdict {
    /** Labour % below the band: more people (hours) than planned are affordable, or the day is short. */
    TOO_FEW,
    /** Labour % inside the band. */
    JUST_RIGHT,
    /** Labour % above the band: the planned shifts cost more than the expected income carries. */
    TOO_MANY
}

/** Where a day's expected sales come from. */
enum class ExpectedSalesSource {
    /** The day's sales target (the weekday target, or the day's own target set in the daily log). */
    DAY_TARGET,
    /** SH-120: no target: the sales forecast from the workplace's history (SalesForecaster). */
    FORECAST,
    /** No target and no forecast: the average takings of the same weekday in the last weeks. */
    RECENT_WEEKDAYS
}

/** The net sales a day is expected to bring, and how many past days that is based on (1 for a target). */
data class ExpectedSales(val amount: Double, val source: ExpectedSalesSource, val basedOnDays: Int = 1)

/** What a day's hint is computed from: its sales target, its income terms and its planned shifts. */
data class StaffingDay(
    val date: LocalDate,
    /** The day's net sales target; 0 when there is none. */
    val salesTarget: Double,
    /** Employer labour cost of the planned, assigned shifts (their cost snapshots). */
    val plannedCost: Double,
    /** Hours of the planned, assigned shifts. */
    val plannedHours: Double,
    /** Hours of the day's shifts nobody is assigned to yet (open or unfilled): not in [plannedCost]. */
    val openHours: Double = 0.0,
    /** SH-193: what [openHours] are estimated at per hour (the workplace's choice); null: the month's own estimate. */
    val openCostPerHour: Double? = null,
    /** SH-95: the commission the operator earns on the day's net sales (a fraction; 0 = none, keeps the sales). */
    val commissionPercent: Double = 0.0,
    /** SH-95: the day's share of the fixed revenue, as in its result. */
    val fixedRevenue: Double = 0.0,
    /** Assigned paid shifts nobody could price (no snapshot, no pay rates): not in [plannedCost] or [plannedHours]. */
    val unpricedShifts: Int = 0,
    val unpricedHours: Double = 0.0,
    /** Whose those shifts are (to set their pay rates). */
    val unpricedUserIds: List<String> = emptyList()
)

/** One day's hint: facts only, the screens word them. */
data class DayStaffingHint(
    val date: LocalDate,
    val verdict: StaffingVerdict,
    /** Planned labour cost (with [openCost]) ÷ [expectedIncome] × 100: what the verdict and the band are about. */
    val labourPercent: Double,
    /** The assigned shifts' cost (their snapshots). */
    val plannedCost: Double,
    val plannedHours: Double,
    val openHours: Double,
    val expectedSales: ExpectedSales,
    val target: LabourTarget,
    /** SH-95: what [labourPercent] is measured against. */
    val base: LabourBase = LabourBase.SALES,
    /** SH-95: the income the expected sales bring ([LabourShare.baseAmount]); the sales without a commission. */
    val expectedIncome: Double = expectedSales.amount,
    /** SH-95: the commission it was computed with (a fraction; 0 = none). */
    val commissionPercent: Double = 0.0,
    /** SH-95: the day's fixed revenue in [expectedIncome]. */
    val fixedRevenue: Double = 0.0,
    /**
     * Employer labour cost to add (positive, too few) or take away (negative, too many) to reach the
     * nearest edge of the band, the smallest change that makes the day just right; 0 inside it.
     */
    val costToBand: Double,
    /**
     * [costToBand] in hours at the day's own cost per hour (or the month's, for a day without paid
     * hours); null when no cost per hour is known. Rounded to half hours, never 0 outside the band.
     */
    val hoursToBand: Double?,
    /**
     * The estimated cost of [openHours] (counted in [labourPercent]); null when they are not priced
     * (no cost per hour known) and left out.
     */
    val openCost: Double? = null,
    /** Assigned shifts left out for want of a pay rate: how many, their hours and whose. */
    val unpricedShifts: Int = 0,
    val unpricedHours: Double = 0.0,
    val unpricedUserIds: List<String> = emptyList()
)

/** A month's hints and how many days fall on each side of the band. */
data class MonthStaffing(
    val target: LabourTarget,
    val days: List<DayStaffingHint>
) {
    val byDate: Map<LocalDate, DayStaffingHint> by lazy { days.associateBy { it.date } }
    val tooFew: Int get() = days.count { it.verdict == StaffingVerdict.TOO_FEW }
    val tooMany: Int get() = days.count { it.verdict == StaffingVerdict.TOO_MANY }
    val justRight: Int get() = days.count { it.verdict == StaffingVerdict.JUST_RIGHT }

    /**
     * The days outside the band from [today] on: what can still be changed (the dashboard's action
     * items). Past days stay in [days] for the daily log.
     */
    fun actionable(today: LocalDate): List<DayStaffingHint> =
        days.filter { it.date >= today && it.verdict != StaffingVerdict.JUST_RIGHT }
}

object StaffingRules {
    /** How many past same weekdays a day without a target looks at. */
    const val RECENT_WEEKS = 4

    /**
     * The sales [date] is expected to bring: its [salesTarget] when there is one; otherwise its
     * [forecast] (SH-120); otherwise the average takings of the same weekday in the last
     * [RECENT_WEEKS] weeks ([salesHistory]: net takings by day, days without takings left out);
     * null when none exists (no hint).
     */
    fun expectedSales(date: LocalDate, salesTarget: Double, salesHistory: Map<LocalDate, Double>, forecast: Double? = null): ExpectedSales? {
        if (salesTarget > 0) return ExpectedSales(salesTarget, ExpectedSalesSource.DAY_TARGET)
        if (forecast != null && forecast > 0) return ExpectedSales(forecast, ExpectedSalesSource.FORECAST)
        val past = (1..RECENT_WEEKS)
            .mapNotNull { weeks -> salesHistory[date.minus(DatePeriod(days = 7 * weeks))] }
            .filter { it > 0 }
        if (past.isEmpty()) return null
        return ExpectedSales(past.average(), ExpectedSalesSource.RECENT_WEEKDAYS, past.size)
    }

    /**
     * [day]'s hint against the income [expected] sales bring (its commission and fixed revenue,
     * SH-95) and the [target] band; the cost (and hours) to the band are the target's share of that income.
     */
    fun hint(
        day: StaffingDay,
        expected: ExpectedSales,
        target: LabourTarget,
        fallbackCostPerHour: Double? = null,
        /** What the day's open hours are estimated at per hour; null: they are not priced. */
        openCostPerHour: Double? = null
    ): DayStaffingHint {
        val income = LabourShare.baseAmount(expected.amount, day.commissionPercent, day.fixedRevenue)
        val openCost = if (day.openHours > 0) openCostPerHour?.times(day.openHours) else null
        val cost = day.plannedCost + (openCost ?: 0.0)
        val percent = cost * 100 / income
        val verdict = when {
            percent > target.high -> StaffingVerdict.TOO_MANY
            percent < target.low -> StaffingVerdict.TOO_FEW
            else -> StaffingVerdict.JUST_RIGHT
        }
        val costToBand = when (verdict) {
            StaffingVerdict.TOO_MANY -> income * target.high / 100 - cost
            StaffingVerdict.TOO_FEW -> income * target.low / 100 - cost
            StaffingVerdict.JUST_RIGHT -> 0.0
        }
        val costPerHour = if (day.plannedCost > 0 && day.plannedHours > 0) day.plannedCost / day.plannedHours else fallbackCostPerHour ?: openCostPerHour
        val hours = costPerHour?.takeIf { it > 0 && verdict != StaffingVerdict.JUST_RIGHT }?.let { roundToHalfHours(costToBand / it) }
        return DayStaffingHint(
            date = day.date,
            verdict = verdict,
            labourPercent = percent,
            plannedCost = day.plannedCost,
            plannedHours = day.plannedHours,
            openHours = day.openHours,
            expectedSales = expected,
            target = target,
            base = LabourShare.baseOf(day.commissionPercent, day.fixedRevenue),
            expectedIncome = income,
            commissionPercent = day.commissionPercent,
            fixedRevenue = day.fixedRevenue,
            costToBand = costToBand,
            hoursToBand = if (verdict == StaffingVerdict.JUST_RIGHT) 0.0 else hours,
            openCost = openCost,
            unpricedShifts = day.unpricedShifts,
            unpricedHours = day.unpricedHours,
            unpricedUserIds = day.unpricedUserIds
        )
    }

    /**
     * Every day of [days] with expected sales, except the [closed] ones. Sales history: past
     * takings by day (the previous month's and this month's so far). A day without paid hours
     * converts its cost gap into hours at the month's average cost per hour. SH-120: [forecasts]
     * stand in for the target of days without one. Open hours count at the month's average cost per
     * hour, else at [teamCostPerHour] (the team's pay terms); without either they are left out.
     * SH-193: a day whose open hours carry the workplace's rate ([StaffingDay.openCostPerHour]) uses it.
     */
    fun forMonth(
        days: List<StaffingDay>,
        salesHistory: Map<LocalDate, Double>,
        target: LabourTarget,
        closed: Set<LocalDate> = emptySet(),
        forecasts: Map<LocalDate, Double> = emptyMap(),
        teamCostPerHour: Double? = null
    ): MonthStaffing {
        val paid = days.filter { it.plannedCost > 0 && it.plannedHours > 0 }
        val monthCostPerHour = paid.sumOf { it.plannedHours }.takeIf { it > 0 }?.let { paid.sumOf { d -> d.plannedCost } / it }
        val openCostPerHour = monthCostPerHour ?: teamCostPerHour
        val hints = days.filter { it.date !in closed }.mapNotNull { day ->
            expectedSales(day.date, day.salesTarget, salesHistory, forecasts[day.date])?.let { hint(day, it, target, monthCostPerHour, day.openCostPerHour ?: openCostPerHour) }
        }
        return MonthStaffing(target, hints.sortedBy { it.date })
    }

    /** Half hours, away from zero: "about 1.5 h". A gap is never shown as 0 h. */
    private fun roundToHalfHours(hours: Double): Double {
        val halves = kotlin.math.ceil(kotlin.math.abs(hours) * 2 - 1e-9).coerceAtLeast(1.0)
        return halves / 2 * kotlin.math.sign(hours)
    }
}
